import type {
  AcceptTicketRequestDTO,
  AddTicketNoteRequestDTO,
  AddTicketNoteResponseDTO,
  AddTicketTaskRequestDTO,
  AddTicketTaskResponseDTO,
  CreateTemplateRequestDTO,
  CreateTicketRequestDTO,
  CreateTicketResponseDTO,
  DeleteNoteResponseDTO,
  DeleteTaskResponseDTO,
  EditTicketNoteRequestDTO,
  GetTicketDetailResponseDTO,
  ListTagsResponseDTO,
  ListTicketActivityResponseDTO,
  ListTicketsRequestDTO,
  ListTicketsResponseDTO,
  ReorderTasksRequestDTO,
  SetTaskStateRequestDTO,
  SetTaskStateResponseDTO,
  Ticket,
  TicketNote,
  TicketTask,
  TicketTemplate,
  UpdateTaskActionRequestDTO,
  UpdateTaskDescriptionRequestDTO,
  UpdateTemplateRequestDTO,
  UpdateTicketAssigneesRequestDTO,
  UpdateTicketDescriptionRequestDTO,
  UpdateTicketDueDateRequestDTO,
  UpdateTicketGroupRequestDTO,
  UpdateTicketLocationRequestDTO,
  UpdateTicketPriorityRequestDTO,
  UpdateTicketStatusRequestDTO,
  UpdateTicketSubscribersRequestDTO,
  UpdateTicketTagsRequestDTO,
  UpdateTicketTitleRequestDTO,
  UpdateTicketUserRequestDTO,
  WorkflowTicketRequestDTO,
  WorkflowTicketResponseDTO
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

// LINK apps/server/src/API/Ticketing/Ticketing.Controller.ts
// LINK apps/server/src/API/Ticketing/Ticketing.Service.ts

export class TicketingAPI {
  // Tickets
  static async listTickets(orgId: number, params: ListTicketsRequestDTO & { sortBy?: string; sortDir?: "asc" | "desc" }) {
    return APIService.Server.get<ListTicketsResponseDTO>(`/ticketing/org/${orgId}/tickets`, params as any);
  }

  static async getTicketDetail(orgId: number, ticketId: number) {
    return APIService.Server.get<GetTicketDetailResponseDTO>(`/ticketing/org/${orgId}/tickets/${ticketId}`);
  }

  static async getTicketByNumber(orgId: number, number: number) {
    return APIService.Server.get<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/by-number/${number}`);
  }

  static async createTicket(orgId: number, body: CreateTicketRequestDTO) {
    return APIService.Server.post<CreateTicketResponseDTO>(`/ticketing/org/${orgId}/tickets`, body);
  }

  static async updateTicketStatus(orgId: number, ticketId: number, body: UpdateTicketStatusRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/status`, body);
  }

  /** Claim an open-to-group ticket for the authenticated member. */
  static async acceptTicket(orgId: number, ticketId: number, body: AcceptTicketRequestDTO) {
    return APIService.Server.post<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/accept`, body);
  }

  static async updateTicketTitle(orgId: number, ticketId: number, body: UpdateTicketTitleRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/title`, body);
  }

  static async updateTicketPriority(orgId: number, ticketId: number, body: UpdateTicketPriorityRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/priority`, body);
  }

  static async updateTicketTags(orgId: number, ticketId: number, body: UpdateTicketTagsRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/tags`, body);
  }

  static async updateTicketAssignees(orgId: number, ticketId: number, body: UpdateTicketAssigneesRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/assignees`, body);
  }

  static async updateTicketSubscribers(orgId: number, ticketId: number, body: UpdateTicketSubscribersRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/subscribers`, body);
  }

  static async updateTicketDescription(orgId: number, ticketId: number, body: UpdateTicketDescriptionRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/description`, body);
  }

  static async updateTicketDueDate(orgId: number, ticketId: number, body: UpdateTicketDueDateRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/due-date`, body);
  }

  static async updateTicketLocation(orgId: number, ticketId: number, body: UpdateTicketLocationRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/location`, body);
  }

  static async updateTicketUser(orgId: number, ticketId: number, body: UpdateTicketUserRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/user`, body);
  }

  /** Re-dispatch the ticket to a different routing group (reopens open-to-group). */
  static async updateTicketGroup(orgId: number, ticketId: number, body: UpdateTicketGroupRequestDTO) {
    return APIService.Server.put<{ ticket: Ticket }>(`/ticketing/org/${orgId}/tickets/${ticketId}/group`, body);
  }

  // Notes
  static async addNote(orgId: number, ticketId: number, body: AddTicketNoteRequestDTO) {
    return APIService.Server.post<AddTicketNoteResponseDTO>(`/ticketing/org/${orgId}/tickets/${ticketId}/notes`, body);
  }

  static async editNote(orgId: number, noteId: number, body: EditTicketNoteRequestDTO) {
    return APIService.Server.put<{ note: TicketNote }>(`/ticketing/org/${orgId}/notes/${noteId}`, body);
  }

  static async deleteNote(orgId: number, noteId: number) {
    return APIService.Server.delete<DeleteNoteResponseDTO>(`/ticketing/org/${orgId}/notes/${noteId}`);
  }

  // Tasks
  static async addTask(orgId: number, ticketId: number, body: AddTicketTaskRequestDTO) {
    return APIService.Server.post<AddTicketTaskResponseDTO>(`/ticketing/org/${orgId}/tickets/${ticketId}/tasks`, body);
  }

  static async setTaskState(orgId: number, taskId: number, body: SetTaskStateRequestDTO) {
    return APIService.Server.put<SetTaskStateResponseDTO>(`/ticketing/org/${orgId}/tasks/${taskId}/state`, body);
  }

  static async updateTaskAction(orgId: number, taskId: number, body: UpdateTaskActionRequestDTO) {
    return APIService.Server.put<{ task: TicketTask }>(`/ticketing/org/${orgId}/tasks/${taskId}/action`, body);
  }

  static async deleteTask(orgId: number, taskId: number) {
    return APIService.Server.delete<DeleteTaskResponseDTO>(`/ticketing/org/${orgId}/tasks/${taskId}`);
  }

  static async updateTaskDescription(orgId: number, taskId: number, body: UpdateTaskDescriptionRequestDTO) {
    return APIService.Server.put<{ task: TicketTask }>(`/ticketing/org/${orgId}/tasks/${taskId}/description`, body);
  }

  static async reorderTasks(orgId: number, ticketId: number, body: ReorderTasksRequestDTO) {
    return APIService.Server.put<{ ok: true }>(`/ticketing/org/${orgId}/tickets/${ticketId}/tasks/reorder`, body);
  }

  // Tags
  static async listTags(orgId: number) {
    return APIService.Server.get<ListTagsResponseDTO>(`/ticketing/org/${orgId}/tags`);
  }

  // Templates
  static async listTemplates(orgId: number) {
    return APIService.Server.get<{ templates: TicketTemplate[] }>(`/ticketing/org/${orgId}/templates`);
  }

  static async getTemplate(orgId: number, templateId: number) {
    return APIService.Server.get<{ template: TicketTemplate }>(`/ticketing/org/${orgId}/templates/${templateId}`);
  }

  static async getDefaultTemplate(orgId: number) {
    return APIService.Server.get<{ template: TicketTemplate | null }>(`/ticketing/org/${orgId}/templates/default`);
  }

  static async createTemplate(orgId: number, body: CreateTemplateRequestDTO) {
    return APIService.Server.post<{ template: TicketTemplate }>(`/ticketing/org/${orgId}/templates`, body);
  }

  static async updateTemplate(orgId: number, templateId: number, body: UpdateTemplateRequestDTO) {
    return APIService.Server.put<{ template: TicketTemplate }>(`/ticketing/org/${orgId}/templates/${templateId}`, body);
  }

  static async disableTemplate(orgId: number, templateId: number) {
    return APIService.Server.delete<{ template: TicketTemplate }>(`/ticketing/org/${orgId}/templates/${templateId}`);
  }

  // Activity
  static async listActivity(orgId: number, ticketId: number, limit = 200) {
    return APIService.Server.get<ListTicketActivityResponseDTO>(`/ticketing/org/${orgId}/tickets/${ticketId}/activity`, { limit });
  }

  // Workflow (Node Handler) Operations
  static async workflowTicketOperation(orgId: number, body: WorkflowTicketRequestDTO) {
    return APIService.Server.post<WorkflowTicketResponseDTO>(`/ticketing/org/${orgId}/tickets/workflow`, body);
  }
}
